import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { AttentionItem } from '../components/AttentionItem.jsx';
import { DashboardStatCard } from '../components/DashboardStatCard.jsx';
import { PageWelcome } from '../components/PageWelcome.jsx';
import { StatusBadge } from '../components/StatusBadge.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { formatMoney } from '../utils/money.js';
import { planTone } from '../utils/statusUi.js';
import { firstName, timeGreeting } from '../utils/userDisplay.js';

const PLAN_COLORS = ['#6366f1', '#3b82f6', '#ec4899', '#10b981', '#f59e0b'];

export function SuperAdminOverviewPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiJson('/api/super-admin/dashboard')
      .then(setStats)
      .catch((e) => setError(e.message));
  }, []);

  const planSlices = useMemo(() => {
    const entries = Object.entries(stats?.subscriptions_by_plan || {});
    const total = entries.reduce((sum, [, count]) => sum + Number(count || 0), 0) || 1;
    let cursor = 0;
    return entries.map(([name, count], i) => {
      const value = Number(count || 0);
      const start = cursor;
      cursor += (value / total) * 100;
      return { name, count: value, start, end: cursor, color: PLAN_COLORS[i % PLAN_COLORS.length] };
    });
  }, [stats]);

  const donutBackground = useMemo(() => {
    if (!planSlices.length) {
      return 'conic-gradient(#e2e8f0 0 100%)';
    }
    const stops = planSlices.map((s) => `${s.color} ${s.start}% ${s.end}%`).join(', ');
    return `conic-gradient(${stops})`;
  }, [planSlices]);

  if (error) {
    return <p className="err">{error}</p>;
  }

  if (!stats) {
    return <p className="muted">Loading dashboard…</p>;
  }

  const currency = stats.revenue_currency || 'cad';
  const activeShare =
    stats.organizations_total > 0
      ? Math.round((Number(stats.active_subscriptions || 0) / stats.organizations_total) * 100)
      : 0;

  return (
    <div>
      <PageWelcome
        title={`${timeGreeting()}, ${firstName(user?.name)}`}
        subtitle="Platform health, organizations, and subscriptions at a glance."
      />

      <div className="kpi-grid" data-tour="tour-dashboard-stats">
        <DashboardStatCard
          label="Organizations"
          value={stats.organizations_total}
          tone="purple"
          hint={stats.organizations_new_this_month ? `+${stats.organizations_new_this_month} this month` : undefined}
          to="/super-admin/organizations"
        />
        <DashboardStatCard
          label="Active Subscriptions"
          value={stats.active_subscriptions ?? 0}
          tone="success"
          hint={`${activeShare}% of total`}
          to="/super-admin/plans"
        />
        <DashboardStatCard
          label="Monthly Recurring Revenue"
          value={formatMoney(stats.mrr_cents, currency)}
          tone="warning"
        />
        <DashboardStatCard
          label="Annual Recurring Revenue"
          value={formatMoney(stats.arr_cents, currency)}
          tone="info"
        />
        <DashboardStatCard
          label="Needs Attention"
          value={stats.organizations_needing_attention}
          tone={stats.organizations_needing_attention > 0 ? 'danger' : 'success'}
          to="/super-admin/organizations"
        />
      </div>

      <div className="dash-grid">
        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">Organizations</h3>
            <Link to="/super-admin/organizations" className="panel-link">
              View all →
            </Link>
          </div>
          {stats.recent_organizations?.length ? (
            <div className="table-wrap" style={{ boxShadow: 'none', border: 0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Plan</th>
                    <th>Users</th>
                    <th>Clients</th>
                    <th>Projects</th>
                    <th>Status</th>
                    <th>Monthly Revenue</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_organizations.map((org) => (
                    <tr key={org.id}>
                      <td>
                        <Link to="/super-admin/organizations">
                          <strong>{org.name}</strong>
                        </Link>
                      </td>
                      <td>
                        {org.subscription?.plan?.name ? (
                          <StatusBadge
                            tone={planTone(org.subscription.plan.name)}
                            label={org.subscription.plan.name}
                            dot={false}
                          />
                        ) : (
                          <span className="muted small">No plan</span>
                        )}
                      </td>
                      <td>{org.users_count ?? '—'}</td>
                      <td>{org.clients_count ?? '—'}</td>
                      <td>{org.projects_count ?? '—'}</td>
                      <td>
                        <StatusBadge
                          status={org.is_suspended ? 'cancelled' : org.subscription?.status || 'inactive'}
                          label={org.is_suspended ? 'Suspended' : org.subscription?.status || 'No subscription'}
                        />
                      </td>
                      <td>{formatMoney(org.monthly_revenue_cents, currency)}</td>
                      <td className="muted small">{org.created_at ? String(org.created_at).slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No organizations yet.</p>
          )}
        </section>

        <section className="panel">
          <h3 className="panel-title">Needs Attention</h3>
          {stats.organizations_needing_attention > 0 ? (
            <div className="attention-list">
              {(stats.attention_organizations || []).length ? (
                stats.attention_organizations.map((org) => (
                  <AttentionItem
                    key={org.id}
                    tone={org.is_suspended ? 'danger' : 'warning'}
                    title={org.name}
                    detail={
                      org.is_suspended
                        ? 'Suspended account'
                        : org.subscription?.status
                          ? `Subscription: ${org.subscription.status}`
                          : 'No subscription'
                    }
                    to="/super-admin/organizations"
                    action="Review"
                  />
                ))
              ) : (
                <AttentionItem
                  tone="danger"
                  title="Organizations need review"
                  detail={`${stats.organizations_needing_attention} require attention`}
                  to="/super-admin/organizations"
                  action="Review"
                />
              )}
            </div>
          ) : (
            <p className="empty-state">All organizations look healthy.</p>
          )}
        </section>
      </div>

      <div className="dash-grid-3">
        <section className="panel">
          <h3 className="panel-title">Subscriptions</h3>
          <div className="donut-wrap">
            <div className="donut" style={{ background: donutBackground }} aria-hidden />
            <ul className="feed-list" style={{ flex: 1 }}>
              {planSlices.length ? (
                planSlices.map((slice) => (
                  <li key={slice.name}>
                    <span>
                      <span className="status-badge-dot" style={{ background: slice.color, display: 'inline-block', marginRight: '0.4rem' }} />
                      {slice.name}
                    </span>
                    <strong>{slice.count}</strong>
                  </li>
                ))
              ) : (
                <li>
                  <span className="muted">No active subscriptions yet.</span>
                </li>
              )}
            </ul>
          </div>
        </section>

        <section className="panel">
          <h3 className="panel-title">Platform Usage</h3>
          <div className="usage-stat">
            <span>Total users</span>
            <strong>{stats.users_total ?? 0}</strong>
          </div>
          <div className="usage-stat">
            <span>Total clients</span>
            <strong>{stats.clients_total ?? 0}</strong>
          </div>
          <div className="usage-stat">
            <span>Total projects</span>
            <strong>{stats.projects_total ?? 0}</strong>
          </div>
          <div className="usage-stat">
            <span>Active plans</span>
            <strong>{stats.plans_active ?? 0}</strong>
          </div>
        </section>

        <section className="panel">
          <h3 className="panel-title">Recent Activity</h3>
          {stats.recent_organizations?.length ? (
            <ul className="feed-list">
              {stats.recent_organizations.slice(0, 6).map((org) => (
                <li key={org.id}>
                  <span>
                    <strong>New organization created</strong>
                    <div className="muted small">{org.name}</div>
                  </span>
                  <span className="muted small">{org.created_at ? String(org.created_at).slice(0, 10) : ''}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty-state">No recent platform activity.</p>
          )}
        </section>
      </div>
    </div>
  );
}
